import * as Yup from "yup";
import { 
  OwnershipType as VehicleOwnershipType,
  ownerInfoValidationSchema,
  leaseFromInfoValidationSchema,
  leaseToInfoValidationSchema
} from "../../../types/ownership";

// Define interfaces locally to avoid import issues
export interface OwnerInfoFormValues {
  company: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssnFein: string;
  bankAccount: string;
  contacts: string;
  email: string;
  website: string;
}

export interface LeaseFromInfoFormValues {
  lessorId: number | null;
  lessorName: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface LeaseToInfoFormValues {
  lesseeId: number | null;
  lesseeName: string | null;
  startDate: string | null;
  endDate: string | null;
}

// Re-export for backward compatibility
export { VehicleOwnershipType };

export const vehicleValidationSchema = Yup.object().shape({
  // Base Vehicle
  name: Yup.string()
    .required("Vehicle name is required")
    .max(100, "Vehicle name cannot be longer than 100 characters"),
  vin: Yup.string()
    .required("VIN is required")
    .max(50, "VIN cannot be longer than 50 characters"),
  make: Yup.string()
    .max(50, "Make cannot be longer than 50 characters"),
  model: Yup.string()
    .max(50, "Model cannot be longer than 50 characters"),
  year: Yup.string()
    .max(4, "Year cannot be longer than 4 characters"),

  // General Section
  unitNumber: Yup.string()
    .max(50, "Unit number cannot be longer than 50 characters"),
  isActive: Yup.boolean(),
  startDate: Yup.date().nullable(),
  endDate: Yup.date().nullable(),

  // Lookup fields
  vehicleTypeId: Yup.number().nullable(),
  vehicleTypeName: Yup.string().nullable(),
  vehicleStatusId: Yup.number().nullable(),
  vehicleStatusName: Yup.string().nullable(),
  divisionId: Yup.number().nullable(),
  divisionName: Yup.string().nullable(),

  // Truck Info Section
  color: Yup.string()
    .max(50, "Color cannot be longer than 50 characters"),
  licensePlate: Yup.string()
    .max(20, "License plate cannot be longer than 20 characters"),
  licenseState: Yup.string()
    .max(2, "License state cannot be longer than 2 characters"),
  fuelCardNumber: Yup.string()
    .max(50, "Fuel card number cannot be longer than 50 characters"),
  fuelType: Yup.string()
    .max(20, "Fuel type cannot be longer than 20 characters"),
  iPassNumber: Yup.string()
    .max(50, "I-Pass number cannot be longer than 50 characters"),
  bestPassNumber: Yup.string()
    .max(50, "Best Pass number cannot be longer than 50 characters"),
  prePassEzPassNumber: Yup.string()
    .max(50, "Pre Pass Ez Pass number cannot be longer than 50 characters"),
  tireSize: Yup.string()
    .max(20, "Tire size cannot be longer than 20 characters"),
  deductTolls: Yup.boolean(),
  deductFuel: Yup.boolean(),

  // Safety Section
  dotInspectionExpiration: Yup.date().nullable(),
  platesExpirationDate: Yup.date().nullable(),
  insuranceExpirationDate: Yup.date().nullable(),
  insuranceValue: Yup.number()
    .min(0, "Insurance value must be non-negative")
    .nullable(),
  escrowStartingBalance: Yup.number()
    .min(0, "Escrow starting balance must be non-negative")
    .nullable(),
  escrowAccountBalance: Yup.number()
    .min(0, "Escrow account balance must be non-negative")
    .nullable(),
  hasCustomWarning: Yup.boolean(),

  // IFTA Section
  iftaHandledByCompany: Yup.boolean(),
  iftaExpirationDate: Yup.date().nullable(),
  nyPermitNumber: Yup.string()
    .max(50, "NY permit number cannot be longer than 50 characters"),
  grossWeight: Yup.number()
    .positive("Gross weight must be greater than 0")
    .nullable(),
  unloadedWeight: Yup.number()
    .positive("Unloaded weight must be greater than 0")
    .nullable(),
  numberOfAxles: Yup.number()
    .positive("Number of axles must be greater than 0")
    .nullable(),

  // Dispatch Section
  dispatcher: Yup.string()
    .max(100, "Dispatcher cannot be longer than 100 characters"),
  dispatchGroup: Yup.string()
    .max(100, "Dispatch group cannot be longer than 100 characters"),
  dispatchCategory: Yup.string()
    .max(100, "Dispatch category cannot be longer than 100 characters"),

  // GPS Section
  gpsType: Yup.string()
    .max(50, "GPS type cannot be longer than 50 characters"),

  // Odometer Section
  lastOdometerReading: Yup.number()
    .min(0, "Last odometer reading must be non-negative")
    .nullable(),
  lastOdometerDate: Yup.date().nullable(),
  estimatedOdometer: Yup.number()
    .min(0, "Estimated odometer must be non-negative")
    .nullable(),

  // Comments Section
  comments: Yup.string()
    .max(1000, "Comments cannot be longer than 1000 characters"),

  // Ownership Section - using shared validation schemas
  ownershipType: Yup.number().required("Ownership type is required"),
  ownerInfo: ownerInfoValidationSchema.when('ownershipType', {
    is: VehicleOwnershipType.NotCompanyOwned,
    then: (schema) => schema.required("Owner information is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  leaseFromInfo: leaseFromInfoValidationSchema.when('ownershipType', {
    is: VehicleOwnershipType.CompanyOwnedLeasedFrom,
    then: (schema) => schema.required("Lease from information is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  leaseToInfo: leaseToInfoValidationSchema.when('ownershipType', {
    is: VehicleOwnershipType.CompanyOwnedLeasedTo,
    then: (schema) => schema.required("Lease to information is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

// Type for form values
export interface VehicleFormValues {
  // Base Vehicle
  name: string;
  vin: string;
  make: string;
  model: string;
  year: string;

  // General Section
  unitNumber: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;

  // Lookup fields
  vehicleTypeId: number | null;
  vehicleTypeName: string | null;
  vehicleStatusId: number | null;
  vehicleStatusName: string | null;
  divisionId: number | null;
  divisionName: string | null;

  // Truck Info Section
  color: string;
  licensePlate: string;
  licenseState: string;
  fuelCardNumber: string;
  fuelType: string;
  iPassNumber: string;
  bestPassNumber: string;
  prePassEzPassNumber: string;
  tireSize: string;
  deductTolls: boolean;
  deductFuel: boolean;

  // Ownership Section - NEW STRUCTURE
  ownershipType: VehicleOwnershipType;
  ownerInfo: OwnerInfoFormValues | null;
  leaseFromInfo: LeaseFromInfoFormValues | null;
  leaseToInfo: LeaseToInfoFormValues | null;

  // Safety Section
  dotInspectionExpiration: string | null;
  platesExpirationDate: string | null;
  insuranceExpirationDate: string | null;
  insuranceValue: number | null;
  escrowStartingBalance: number | null;
  escrowAccountBalance: number | null;
  hasCustomWarning: boolean;

  // IFTA Section
  iftaHandledByCompany: boolean;
  iftaExpirationDate: string | null;
  nyPermitNumber: string;
  grossWeight: number | null;
  unloadedWeight: number | null;
  numberOfAxles: number | null;

  // Dispatch Section
  dispatcher: string;
  dispatchGroup: string;
  dispatchCategory: string;

  // GPS Section
  gpsType: string;

  // Odometer Section
  lastOdometerReading: number | null;
  lastOdometerDate: string | null;
  estimatedOdometer: number | null;

  // Comments Section
  comments: string;
} 